import useFetchFilteredResults from 'js/hooks/use-fetch-filtered-results';

import EmptyList from 'components/empty-list/EmptyList';
import FilterLayout from 'components/filter-layout/FilterLayout';
import Form from 'components/form/Form';
import TabList from 'components/tabs/TabList';
import TabListItem from 'components/tabs/TabListItem';
import Link from 'components/link/Link';
import Heading from 'components/heading/Heading';
import Spinner from 'components/spinner/Spinner';
import ProposalShortcuts from 'components/proposal-shortcuts/ProposalShortcuts';
import ContentContainer from 'components/content-container/ContentContainer';

import ProposalGroup from './ProposalGroup';

import { IProposalListPage } from 'source/NFR.types';

type ProposalListFilterState = Pick<IProposalListPage, 'emptyList' | 'filterLayout' | 'groups'>;

const ProposalListPage: React.FC<IProposalListPage> = ({
  emptyList,
  filterLayout,
  form,
  groups = [],
  title,
  sectionTitle,
  topFilter,
  fetchFilteredResultsEndpoint,
  proposalShortcuts
}) => {
  const [isLoading, { emptyList: emptyListState, filterLayout: filterLayoutState, groups: groupsState }, fetchResults] =
    useFetchFilteredResults<ProposalListFilterState>(
      { emptyList, filterLayout, groups },
      fetchFilteredResultsEndpoint,
      form.endpoint,
      topFilter.timeframe ? { ...topFilter.timeframe, value: String(topFilter.timeframe.value) } : undefined
    );

  return (
    <Form className="proposal-list-page" showSubmitButton={false} {...form}>
      <ContentContainer className="proposal-list-page--top">
        {title && <h1 className="proposal-list-page--title">{title}</h1>}
        {proposalShortcuts && <ProposalShortcuts {...proposalShortcuts} />}
      </ContentContainer>
      <FilterLayout
        className="proposal-list-page--hide-underline-overflow"
        {...filterLayoutState}
        includeBottomBorder={false}
        fetchFilteredResults={fetchResults}
        topContent={
          topFilter && topFilter.options ? (
            <TabList className="proposal-list-page--tab-list">
              {topFilter.options?.map(({ isCurrent, link, disabled }, index) => (
                <TabListItem
                  key={link.url}
                  isCurrent={isCurrent}
                  length={topFilter.options.length}
                  index={index}
                  currentIndex={topFilter.options.findIndex(({ isCurrent }) => isCurrent)}
                  disabled={disabled}
                >
                  <Link theme={isCurrent ? 'black' : []} {...link} />
                </TabListItem>
              ))}
            </TabList>
          ) : null
        }
        isLoading={isLoading}
      >
        <>
          {sectionTitle && <Heading>{sectionTitle}</Heading>}
          {groupsState && groupsState.length > 0 ? (
            groupsState?.map((group, index) => (
              <ProposalGroup headingLevel={sectionTitle ? 3 : 2} key={group.title + String(index)} {...group} />
            ))
          ) : (
            <EmptyList {...emptyListState} />
          )}
          <Spinner isActive={isLoading} />
        </>
      </FilterLayout>
      {topFilter.timeframe && (
        <input
          type="hidden"
          id={topFilter.timeframe.id}
          name={topFilter.timeframe.name}
          value={topFilter.timeframe.value}
        />
      )}
    </Form>
  );
};

ProposalListPage.displayName = 'ProposalListPage';

export default ProposalListPage;
